import PropTypes from 'prop-types';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';

import { menuStyles, MENU_SHADOW } from 'components/simple/MenuPanel';
import { SearchIcon } from 'assets/icons';

const LISTBOX_INDENT = 20;

const styles = {
  autocomplete: ({ palette, typography, spacing }) => ({
    width: '100%',
    maxWidth: 880,
    '.MuiFormControl-root .MuiInputBase-root': {
      ...typography.button,
      fontFamily: `${typography.button.fontFamily} !important`,
      borderRadius: '60px !important',
      paddingInline: `${spacing(2)} !important`,
      '.MuiInputBase-input': {
        padding: 'unset',
      },
      '.MuiOutlinedInput-notchedOutline': {
        borderColor: `${palette.colors.darkPale} !important`,
      },
      '&.Mui-error': {
        '.MuiOutlinedInput-notchedOutline': {
          borderColor: `${palette.error.main} !important`,
        },
      },
    },
  }),
  icon: ({ palette }) => ({
    width: 32,
    height: 32,
    color: palette.colors.darkGrey,
  }),
  listboxPaper: {
    width: `calc(100% - ${LISTBOX_INDENT * 2}px)`,
    marginX: `${LISTBOX_INDENT}px`,
    borderRadius: 2.5,
    boxShadow: MENU_SHADOW,
    '.MuiAutocomplete-noOptions': {
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      alignItems: 'center',
    },
  },
  listbox: ({ palette }) => ({
    padding: 'unset',
    border: `1px solid ${palette.colors.darkPale}`,
    '.MuiAutocomplete-option': {
      justifyContent: 'space-between',
      padding: 2,
      ...menuStyles.autocompleteOption({ palette }),
    },
  }),
  option: ({ palette }) => ({
    borderBottom: `1px solid ${palette.colors.darkPale}`,
    '&:last-of-type': {
      borderBottom: 'unset',
    },
  }),
};

export const SearchAutocomplete = ({
  renderOptionContent,
  innerRef,
  error,
  searchValue,
  setSearchValue,
  ...autocompleteProps
}) => {
  return (
    <Autocomplete
      {...autocompleteProps}
      ref={innerRef}
      inputValue={searchValue}
      onInputChange={(_event, value) => setSearchValue(value)}
      renderInput={(params) => (
        <TextField
          {...params}
          error={!!error}
          helperText={error?.message}
          placeholder="Search"
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: <Box component={SearchIcon} sx={styles.icon} />,
            },
          }}
        />
      )}
      sx={styles.autocomplete}
      slotProps={{
        listbox: { sx: styles.listbox },
        paper: { sx: styles.listboxPaper },
      }}
      filterOptions={(x) => x}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;

        return (
          <Box component="li" key={key} {...optionProps} sx={styles.option}>
            {renderOptionContent(option)}
          </Box>
        );
      }}
      loadingText={<Typography variant="body2">Loading...</Typography>}
      noOptionsText={
        <>
          <Typography variant="body2-bold">No results found</Typography>
          <Typography variant="body2" align="center">
            No results found. If you didn’t find the user that you need, you can invite them in the side menu or just
            wait for them to activate their account.
          </Typography>
        </>
      }
    />
  );
};

SearchAutocomplete.propTypes = {
  id: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  customStyles: PropTypes.shape({}),
  renderOptionContent: PropTypes.func.isRequired,
  innerRef: PropTypes.func,
  error: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      type: PropTypes.string,
      message: PropTypes.string,
      ref: PropTypes.shape({}),
    }),
  ]),
  searchValue: PropTypes.string.isRequired,
  setSearchValue: PropTypes.func.isRequired,
};
